import styled, { css, keyframes } from 'styled-components';

import { Section } from '@/js/libs/ui/src/V2';

const ANIMATION_DURATION_MS = 200;

const badgeShow = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const badgeHide = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

const overlayShow = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const overlayHide = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
  }
`;

export const ToggleButton = styled.button<{ $isActive: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s,
    background-color 0.2s;
  ${({ theme }) => theme.shadows.medium};

  ${({ $isActive, theme }) =>
    $isActive
      ? css`
          background-color: ${theme.colors.main};
          color: ${theme.colors.grey00_4};
        `
      : css`
          background-color: ${theme.colors.grey05_1};
          color: ${theme.colors.grey100_1};
        `}

  &:hover {
    transform: scale(1.05);
  }
`;

export const ToggleBadge = styled.span<{ $visible: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.main};
  color: ${({ theme }) => theme.colors.grey00_4};
  ${({ theme }) => theme.typography.textSmall};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${({ $visible }) => ($visible ? badgeShow : badgeHide)} ${ANIMATION_DURATION_MS}ms ease forwards;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const OverlayContainer = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 20px;
  z-index: 9000;
  width: 560px;
  height: 100%;
  max-height: calc(100vh - 120px);
  background: ${({ theme }) => theme.colors.grey00_1};
  border-radius: ${({ theme }) => theme.borders.radiusMedium}px;
  ${({ theme }) => theme.shadows.large};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${({ $visible }) => ($visible ? overlayShow : overlayHide)} ${ANIMATION_DURATION_MS}ms ease forwards;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const SectionContainer = styled(Section)`
  flex: 1;
`;

export const SearchContainer = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey10_1};
  background: ${({ theme }) => theme.colors.grey00_1};
`;

export const TranslationList = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.grey00_1};
`;

export const TranslationItem = styled.div`
  padding: 14px 20px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey05_1};
  background: ${({ theme }) => theme.colors.grey00_1};

  &:hover {
    background: ${({ theme }) => theme.colors.grey01_1};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TranslationKey = styled.div`
  ${({ theme }) => theme.typography.textSmall};
  font-family: monospace;
  color: ${({ theme }) => theme.colors.grey70_1};
  margin-bottom: 4px;
  word-break: break-all;
`;

export const TranslationValue = styled.div`
  ${({ theme }) => theme.typography.textMedium};
  color: ${({ theme }) => theme.colors.black_1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey100_1};
  ${({ theme }) => theme.typography.textMedium};
  background: ${({ theme }) => theme.colors.grey00_1};
`;

export const ToggleIcon = styled.span`
  font-size: 24px;
`;
